// Pure helpers for the review panel (CONTEXT.md "Check", "Review",
// "Proofread"): which runs make up the card's latest check, the findings it
// lists by category, and how triage moves between them.
import { htmlText, plainText } from '../../services/findingText';

export const PROOFREAD_TYPES = ['spelling', 'grammar', 'punctuation', 'word_choice', 'script'];

// Categories in tab order, with their colour.
export const CATEGORIES = [
    { key: 'proofread', label: 'Proofread', color: '#0f766e' },
    { key: 'factual', label: 'Factual', color: '#dc2626' },
    { key: 'outdated', label: 'Outdated', color: '#d97706' },
    { key: 'style', label: 'Style', color: '#3b5bdb' },
    { key: 'suggestion', label: 'Suggestions', color: '#7c3aed' },
];

const SUBTYPES = {
    spelling: 'Spelling',
    grammar: 'Grammar',
    punctuation: 'Punctuation',
    word_choice: 'Word choice',
    script: 'Look-alike letters',
    factual: 'Factual',
    outdated: 'Outdated',
    style: 'Style',
    suggestion: 'Addition',
};

export const categoryOf = type => (PROOFREAD_TYPES.includes(type) ? 'proofread' : type);
export const subtypeOf = type => SUBTYPES[type] || type;
export const categoryInfo = key => CATEGORIES.find(c => c.key === key);

// A full review's proofread run is created just before its review run.
const PAIR_SECONDS = 60;

// The card's latest check: { mode: 'proofread' | 'full', runs }. A full
// review is its review run plus the proofread run started with it (none
// for reviews from before proofreading existed). Null when the card was
// never checked or the latest check was cancelled.
export function latestCheck(runs) {
    const newestFirst = [...runs].sort((a, b) => b.id - a.id);
    const newest = newestFirst[0];
    if (!newest || newest.status === 'cancelled') return null;
    if (newest.kind === 'proofread') return { mode: 'proofread', runs: [newest] };
    const partner = newestFirst.find(run => (
        run.kind === 'proofread' && run.id < newest.id
        && Math.abs((newest.created_at || 0) - (run.created_at || 0)) <= PAIR_SECONDS
    ));
    return { mode: 'full', runs: partner ? [partner, newest] : [newest] };
}

// 'idle' | 'running' | 'error' (every run failed) | 'done'.
export function checkPhase(check) {
    if (!check) return 'idle';
    if (check.runs.some(run => run.status === 'running')) return 'running';
    if (check.runs.every(run => run.status === 'error')) return 'error';
    return 'done';
}

export const failedRuns = check => (check ? check.runs.filter(run => run.status === 'error') : []);

export const hasApplicableFix = f => !!(f.anchor_html && f.replacement_html);
export const isOpen = f => !f.resolution;

// Where a finding sits in the body's text, for document order.
function textPosition(text, finding) {
    const needle = plainText(finding.anchor_html) || (finding.claim_text || '').trim();
    if (!needle) return Infinity;
    const at = text.indexOf(needle);
    if (at !== -1) return at;
    const loose = text.toLowerCase().indexOf(needle.toLowerCase());
    return loose === -1 ? Infinity : loose;
}

const CATEGORY_ORDER = Object.fromEntries(CATEGORIES.map((c, i) => [c.key, i]));

// The findings of the check's finished runs, tagged with their run and
// category, ordered by category and then by where they sit in the body
// the check read (so the order holds while fixes change the text).
export function checkFindings(check) {
    if (!check) return [];
    const done = check.runs.filter(run => run.status === 'done');
    const text = htmlText(done[0]?.content_snapshot || '');
    const tagged = done.flatMap(run => (run.findings || []).map(f => ({
        ...f,
        runId: run.id,
        kind: run.kind,
        key: `${run.id}-${f.id}`,
        category: categoryOf(f.type),
        subtype: subtypeOf(f.type),
    })));
    const positions = new Map(tagged.map(f => [f.key, textPosition(text, f)]));
    return tagged
        .map((f, i) => ({ f, i }))
        .sort((a, b) => (
            (CATEGORY_ORDER[a.f.category] ?? 99) - (CATEGORY_ORDER[b.f.category] ?? 99)
            || positions.get(a.f.key) - positions.get(b.f.key)
            || a.i - b.i
        ))
        .map(({ f }) => f);
}

// One group per category that has findings, in tab order.
export function categoryGroups(findings) {
    return CATEGORIES
        .map(c => ({ ...c, findings: findings.filter(f => f.category === c.key) }))
        .filter(g => g.findings.length > 0)
        .map(g => ({ ...g, open: g.findings.filter(isOpen).length }));
}

// The next open finding after `key` in order, wrapping round; `resolved`
// are keys about to be resolved.
export function nextOpenAfter(findings, key, resolved = []) {
    const i = findings.findIndex(f => f.key === key);
    const after = [...findings.slice(i + 1), ...findings.slice(0, Math.max(i, 0))];
    return after.find(f => isOpen(f) && !resolved.includes(f.key)) || null;
}

// The open finding `step` places from `key` (↑ / ↓), across categories.
export function stepOpen(findings, key, step) {
    const open = findings.filter(isOpen);
    if (!open.length) return null;
    const i = open.findIndex(f => f.key === key);
    if (i === -1) return open[0];
    return open[(i + step + open.length) % open.length];
}

export const firstOpenIn = (findings, category) => findings.find(f => f.category === category && isOpen(f)) || null;

// The category `step` tabs from `category` (← / →).
export function stepCategory(groups, category, step) {
    if (!groups.length) return null;
    const i = Math.max(0, groups.findIndex(g => g.key === category));
    return groups[(i + step + groups.length) % groups.length].key;
}

// The walk through a course check: the next card (in reading order, after
// `cardId`, wrapping round) whose latest check has open findings. `cards`
// is the course's [{ card_id, topic_id, open }] in reading order.
export function nextCardWithFindings(cards, cardId) {
    const i = cards.findIndex(c => c.card_id === cardId);
    const after = [...cards.slice(i + 1), ...cards.slice(0, Math.max(i, 0))];
    return after.find(c => c.open > 0 && c.card_id !== cardId) || null;
}

export const otherCardsWithFindings = (cards, cardId) => cards.filter(c => c.open > 0 && c.card_id !== cardId).length;

// Cosmetic progress for a running check: the server reports no stages.
// A full review proofreads first; `reviewSeconds` counts from the moment
// its proofread run finished (null while it runs).
const PROOF_STAGES = ['Checking spelling', 'Checking grammar and punctuation', 'Compiling results'];
const REVIEW_STAGES = [
    'Extracting claims',
    'Verifying against sources',
    'Checking for outdated information',
    'Generating suggestions',
    'Compiling results',
];
const PROOF_SECONDS = 21;
const REVIEW_SECONDS = 100;
const PROGRESS_CAP = 0.92;
const PROOF_SHARE = 0.25;

const stageAt = (stages, fraction) => stages[Math.min(stages.length - 1, Math.floor(fraction * stages.length))];

export function checkProgress(mode, seconds, reviewSeconds) {
    const proofFraction = Math.min(1, seconds / PROOF_SECONDS);
    if (mode === 'proofread') {
        return { stage: stageAt(PROOF_STAGES, proofFraction), fraction: proofFraction * PROGRESS_CAP };
    }
    if (reviewSeconds == null) {
        return { stage: 'Proofreading', fraction: proofFraction * PROOF_SHARE };
    }
    const reviewFraction = Math.min(1, reviewSeconds / REVIEW_SECONDS);
    return {
        stage: stageAt(REVIEW_STAGES, reviewFraction),
        fraction: PROOF_SHARE + reviewFraction * (PROGRESS_CAP - PROOF_SHARE),
    };
}

// `runs` with each updated run swapped in by id.
export const replaceRuns = (runs, updated) => runs.map(run => updated.find(u => u.id === run.id) || run);

// `runs` with the given findings of one run marked resolved, for an
// optimistic update before the server confirms.
export function markResolved(runs, runId, findingIds, resolution) {
    const resolvedAt = new Date().toISOString();
    return runs.map(run => (run.id !== runId ? run : {
        ...run,
        findings: (run.findings || []).map(f => (
            findingIds.includes(f.id) ? { ...f, resolution, resolved_at: resolvedAt } : f
        )),
    }));
}
